import { ChainData, ChainType, Token } from "@0xsquid/squid-types";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { getClient } from "../../core/client";
import { keys } from "../../core/queries/queries-keys";
import { StellarApiClient } from "../../services/external/stellarApiClient";
import { getStellarHorizonApiUrl } from "../../services/internal/stellarService";

const DEFAULT_REFETCH_INTERVAL = 20_000;

interface Params {
  address: string | undefined;
  token: Token | undefined;
  chain: ChainData | undefined;
}

export function useStellarAccountActivation({
  address,
  chain,
  token,
}: Params): {
  accountActivatedInfo: UseQueryResult<{
    isActivated: boolean;
    reserveBaseBn: bigint;
  } | null>;
} {
  /**
   * Checks if the destination account exists on the Stellar network
   * Stellar accounts need to have a minimum balance before they can receive payments
   */
  const accountActivatedInfo = useQuery({
    queryKey: keys().stellarAccountActivatedInfo(
      address,
      chain?.chainId,
      chain?.chainType
    ),
    queryFn: async () => {
      if (
        chain?.chainType !== ChainType.STELLAR ||
        token?.type !== ChainType.STELLAR
      ) {
        return null;
      }

      if (!address) {
        throw new Error("Destination address is required");
      }

      const horizonApiUrl = getStellarHorizonApiUrl(chain);

      if (!horizonApiUrl) {
        throw new Error("Invalid Horizon API URL");
      }

      const stellarApiClient = new StellarApiClient(horizonApiUrl);

      const reserveBase = await stellarApiClient.getBaseReserve();
      // Stellar accounts require two base reserves to be activated
      // https://developers.stellar.org/docs/learn/fundamentals/lumens#minimum-balance
      const accountReserveBase = reserveBase * BigInt(2);

      const stellarRpcClient = await getClient<ChainType.STELLAR>(chain);
      const isActivated = await stellarRpcClient.isAccountActivated(address);

      return {
        isActivated,
        reserveBaseBn: accountReserveBase,
      };
    },
    enabled: !!address && chain?.chainType === ChainType.STELLAR,
    refetchInterval: DEFAULT_REFETCH_INTERVAL,
    refetchOnWindowFocus: true,
  });

  return {
    accountActivatedInfo,
  };
}
